import { useEffect, useRef, useState } from 'react';
import {
  RedirectToSignIn,
  SignedIn,
  SignedOut,
  UserButton,
} from '@clerk/clerk-react';
import { createFileRoute, Link, Navigate, Outlet, useMatchRoute, useNavigate } from '@tanstack/react-router';
import { Bell, CheckCheck, Settings, User } from 'lucide-react';

import { Button } from '../../components/ui';

import { DevOverridesProvider } from '../../components/dev/DevToolsContext';
import { DevToolsPanel } from '../../components/dev/DevToolsPanel';
import { CreateClubModal } from '../../components/CreateClubModal';
import { NavbarQueuePopover } from '../../components/NavbarQueuePopover';
import {
  AppShell,
  MobileMenuButton,
  Navbar,
  NavbarSection,
  Sidebar,
  SidebarItem,
} from '../../components/ui';
import { usePresence } from '../../hooks/usePresence';
import { useSocket } from '../../hooks/useSocket';
import { trpc } from '../../lib/trpc';

export const Route = createFileRoute('/app')({
  component: AppLayout,
});

function AppLayout() {
  // Track user presence (sets status to "online")
  usePresence();
  // Connect socket
  useSocket();

  const matchRoute = useMatchRoute();
  const { data: user, isLoading } = trpc.user.me.useQuery();
  const [createClubOpen, setCreateClubOpen] = useState(false);

  const isOnboarding = !!matchRoute({ to: '/app/onboarding', fuzzy: true });

  // Redirect to onboarding if not completed
  if (!isLoading && user && !user.onboardingCompletedAt && !isOnboarding) {
    return <Navigate to="/app/onboarding/step-1" />;
  }

  // Redirect away from onboarding if already completed
  if (!isLoading && user && user.onboardingCompletedAt && isOnboarding) {
    return <Navigate to="/app" />;
  }

  // If on onboarding route, render Outlet directly (no app shell)
  if (isOnboarding) {
    return (
      <>
        <SignedIn>
          <Outlet />
        </SignedIn>
        <SignedOut>
          <RedirectToSignIn />
        </SignedOut>
      </>
    );
  }

  return (
    <>
      <SignedIn>
        <DevOverridesProvider>
          <AppShell
            sidebar={<AppSidebar onCreateClub={() => setCreateClubOpen(true)} />}
            navbar={<AppNavbar />}
          >
            <Outlet />
          </AppShell>
          <CreateClubModal open={createClubOpen} onClose={() => setCreateClubOpen(false)} />
        </DevOverridesProvider>
      </SignedIn>
      <SignedOut>
        <RedirectToSignIn />
      </SignedOut>
    </>
  );
}

function AppSidebar({ onCreateClub }: { onCreateClub: () => void }) {
  const matchRoute = useMatchRoute();
  const { data: clubs, isLoading } = trpc.club.getMyClubs.useQuery();

  const isDiscoverActive = !!matchRoute({ to: '/app/clubs' });

  return (
    <Sidebar
      logo={
        <Link to="/app">
          <img src="/logos/logo_icon.svg" alt="Rivaled" className="size-8" />
        </Link>
      }
      onAddClick={onCreateClub}
      discoverHref="/app/clubs"
      isDiscoverActive={isDiscoverActive}
    >
      {isLoading
        ? [1, 2].map((i) => (
            <div
              key={i}
              className="w-full h-12 rounded-xl bg-surface-800 animate-pulse"
            />
          ))
        : (
        clubs?.map((club) => {
          const isClubActive = !!matchRoute({ to: '/app/clubs/$slug', params: { slug: club.slug }, fuzzy: true });
          return (
          <SidebarItem
            key={club.id}
            label={club.name}
            subtitle={club.role}
            shortcut={club.name.charAt(0).toUpperCase()}
            href={`/app/clubs/${club.slug}`}
            isActive={isClubActive}
          >
            {club.avatarUrl && (
              <img
                src={club.avatarUrl}
                alt={club.name}
                className="w-10 h-10 rounded-lg object-cover"
              />
            )}
          </SidebarItem>
          );
        })
      )}
    </Sidebar>
  );
}

function AppNavbar() {
  const { data: user } = trpc.user.me.useQuery();

  return (
    <Navbar>
      <NavbarSection>
        <MobileMenuButton />
      </NavbarSection>

      <NavbarQueuePopover />

      <NavbarSection>
        <div className="flex items-center gap-3">
          {import.meta.env.DEV && <DevToolsPanel />}
          <NotificationBell />

          <Link
            to="/app/profile"
            className="text-sm font-medium text-surface-200 hover:text-white transition-colors hidden sm:block"
          >
            {user?.username}
          </Link>
          <UserButton
            appearance={{
              elements: {
                avatarBox: 'size-8',
              },
            }}
          >
            <UserButton.MenuItems>
              <UserButton.Link
                label="Profile"
                labelIcon={<User className="size-4" />}
                href="/app/profile"
              />
              <UserButton.Link
                label="Settings"
                labelIcon={<Settings className="size-4" />}
                href="/app/settings"
              />
            </UserButton.MenuItems>
          </UserButton>
        </div>
      </NavbarSection>
    </Navbar>
  );
}

function NotificationBell() {
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const utils = trpc.useUtils();
  const socket = useSocket();

  const { data: unreadData } = trpc.notification.getUnreadCount.useQuery(undefined, {
    refetchInterval: 30000,
  });

  const { data: notificationsData } = trpc.notification.list.useQuery(
    { limit: 5 },
    { enabled: open },
  );

  const markReadMutation = trpc.notification.markRead.useMutation({
    onSuccess: () => {
      utils.notification.list.invalidate();
      utils.notification.getUnreadCount.invalidate();
    },
  });

  const count = unreadData?.count ?? 0;
  const notifications = notificationsData?.items ?? [];

  // Listen for real-time notifications
  useEffect(() => {
    const s = socket.socket;
    if (!s) return;

    const handleNewNotification = () => {
      utils.notification.getUnreadCount.invalidate();
      utils.notification.list.invalidate();
    };

    s.on('notification:new', handleNewNotification);
    return () => {
      s.off('notification:new', handleNewNotification);
    };
  }, [socket.socket, utils]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div className="relative" ref={dropdownRef}>
      <Button
        variant="ghost"
        size="icon-md"
        className="relative text-surface-400 hover:text-white"
        aria-label="Notifications"
        onClick={() => setOpen(!open)}
      >
        <Bell className="size-5" />
        {count > 0 && (
          <span className="absolute top-1 right-1 flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full bg-brand-500 text-[10px] font-bold text-surface-900">
            {count > 9 ? '9+' : count}
          </span>
        )}
      </Button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-80 rounded-xl bg-surface-800 border border-surface-700/50 shadow-2xl z-50 overflow-hidden">
          <div className="px-4 py-3 border-b border-surface-700/50">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-white">Notifications</h3>
              <div className="flex items-center gap-2">
                {count > 0 && (
                  <button
                    type="button"
                    className="text-xs text-brand-400 hover:text-brand-300 font-medium flex items-center gap-1"
                    onClick={() => markReadMutation.mutate({})}
                  >
                    <CheckCheck className="size-3" />
                    Mark all read
                  </button>
                )}
                <button
                  type="button"
                  className="text-xs text-surface-400 hover:text-surface-300 font-medium"
                  onClick={() => {
                    setOpen(false);
                    navigate({ to: '/app/notifications' });
                  }}
                >
                  View all
                </button>
              </div>
            </div>
          </div>

          <div className="max-h-80 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="py-8 text-center">
                <Bell className="size-6 text-surface-500 mx-auto mb-2" />
                <p className="text-sm text-surface-400">No notifications</p>
              </div>
            ) : (
              notifications.map((notification) => (
                <button
                  key={notification.id}
                  type="button"
                  onClick={() => {
                    if (!notification.isRead) {
                      markReadMutation.mutate({ notificationId: notification.id });
                    }
                    setOpen(false);
                    const d = notification.data as Record<string, string> | null;
                    if (d?.clubSlug) {
                      navigate({ to: '/app/clubs/$slug', params: { slug: d.clubSlug } });
                    }
                  }}
                  className={`w-full flex items-start gap-3 px-4 py-3 border-b border-surface-700/30 last:border-b-0 hover:bg-surface-700/30 transition-colors text-left ${
                    notification.isRead ? 'opacity-60' : ''
                  }`}
                >
                  <div className={`size-2 rounded-full mt-1.5 shrink-0 ${notification.isRead ? 'bg-transparent' : 'bg-brand-500'}`} />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-surface-200 truncate">{notification.title}</p>
                    {notification.body && (
                      <p className="text-[10px] text-surface-500 truncate mt-0.5">{notification.body}</p>
                    )}
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
